// ServiceRow.js — presentational row for one service inside a flavor card.
// Pure/presentational: all state & math arrive via props; clicks call callbacks.
import React from 'react';
import { FREQS } from './serviceFlavorsModel';

const MODE_LABEL = { in: 'Included', extra: 'Optional', off: 'Off' };

const isMaint = (role) => role === 'Maint' || role === 'Maintenance';

// Small stepper button. Hidden entirely when no handler / read-only dial.
function Stepper({ label, onClick }) {
  return (
    <button
      type="button"
      className="sf-step"
      aria-label={label}
      onClick={onClick}
      style={{
        width: 22, height: 22, lineHeight: '20px', textAlign: 'center',
        border: '1px solid var(--sf-line-2)', borderRadius: 6,
        background: 'var(--sf-surface)', color: 'var(--sf-ink-3)',
        cursor: 'pointer', fontSize: 14, padding: 0,
      }}
    >
      {label === 'increase' ? '+' : '−'}
    </button>
  );
}

// One dial block: caption + value (+ optional steppers + sub-caption).
function Dial({ caption, value, subCaption, onInc, onDec, isNum }) {
  const showSteppers = onInc && onDec;
  return (
    <div
      className="sf-dial"
      style={{
        flex: 1, minWidth: 0, background: 'var(--sf-inset)',
        border: '1px solid var(--sf-line)', borderRadius: 10, padding: '8px 10px',
      }}
    >
      <div
        className="sf-dial-cap"
        style={{ fontSize: 10.5, letterSpacing: '.04em', textTransform: 'uppercase',
          color: 'var(--sf-muted)', marginBottom: 4 }}
      >
        {caption}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
        {showSteppers && <Stepper label="decrease" onClick={onDec} />}
        <span
          className={`sf-dial-val${isNum ? ' num' : ''}`}
          style={{ fontSize: 14, fontWeight: 600, color: 'var(--sf-ink-2)',
            textAlign: 'center', flex: 1, minWidth: 0 }}
        >
          {value}
        </span>
        {showSteppers && <Stepper label="increase" onClick={onInc} />}
      </div>
      {subCaption ? (
        <div style={{ fontSize: 10.5, color: 'var(--sf-muted-2)', marginTop: 4 }}>{subCaption}</div>
      ) : null}
    </div>
  );
}

export default function ServiceRow({
  meta, st, totalStr, note,
  freqLabel, durLabel, durCaption, freqCaption, feeStr,
  onCycleMode, onStepFreq, onStepDur,
}) {
  const role = meta.role;
  const delivery = meta.delivery;            // 'labor' | 'flat' | 'tree'
  const mode = st.mode;                      // 'in' | 'extra' | 'off'
  const off = mode === 'off';
  const extra = mode === 'extra';

  const fi = typeof st.fi === 'number' ? st.fi : 4;
  const freqEntry = FREQS[Math.max(0, Math.min(fi, FREQS.length - 1))];

  // ---- LEFT dial: Frequency / Schedule ----
  const leftCaption = freqCaption || (
    isMaint(role) ? 'Schedule'
      : (delivery === 'flat' || delivery === 'tree') ? 'Visits / yr'
        : 'Frequency'
  );
  const leftValue = freqLabel != null ? freqLabel : (
    isMaint(role) ? `${st.days}×/wk`
      : (delivery === 'flat' || delivery === 'tree') ? `${freqEntry.v} visits`
        : freqEntry.f
  );
  // Maint schedule is read-only here (set in the modal) → no steppers.
  const leftSteppers = !isMaint(role);

  // ---- RIGHT dial: Hours / Tree count / Per-visit ----
  const rightCaption = durCaption || (
    delivery === 'tree' ? 'Tree count'
      : delivery === 'flat' ? 'Per visit'
        : 'Hours / visit'
  );
  const rightValue = durLabel != null ? durLabel : (
    delivery === 'tree' ? `${st.trees} trees`
      : delivery === 'flat' ? '—'
        : `${st.hrsPerVisit} hrs`
  );
  // Flat per-visit price is read-only → no steppers.
  const rightSteppers = delivery !== 'flat';
  const rightIsNum = delivery !== 'flat' || durLabel != null;

  // ---- Box styling per mode ----
  const boxStyle = {
    border: `1px solid ${off ? 'var(--sf-line)' : extra ? 'var(--sf-warn-soft-2)' : 'var(--sf-line-2)'}`,
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
    background: off ? 'var(--sf-off)' : extra ? 'var(--sf-warn-soft)' : 'var(--sf-surface)',
    opacity: off ? 0.6 : 1,
  };

  const pillStyle = {
    border: 'none',
    cursor: 'pointer',
    borderRadius: 999,
    padding: '3px 10px',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '.02em',
    color: off ? 'var(--sf-muted)' : extra ? '#fff' : '#fff',
    background: off ? 'var(--sf-inset-2)' : extra ? 'var(--sf-warn)' : 'var(--sf-green)',
  };

  return (
    <div className={`sf-svc-row sf-svc-${(role || '').toLowerCase()} is-${mode}`} style={boxStyle}>
      {/* Header: color dot + label + total + mode pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span
          className="sf-dot"
          style={{ width: 9, height: 9, borderRadius: '50%', flex: '0 0 auto',
            background: meta.accent || 'var(--sf-green-2)' }}
        />
        <span className="sf-svc-label" style={{ fontWeight: 700, color: 'var(--sf-ink)', fontSize: 13.5 }}>
          {meta.label}
        </span>
        <span className="sf-svc-total num" style={{ marginLeft: 'auto', fontSize: 12,
          color: 'var(--sf-muted)' }}>
          {totalStr}
        </span>
        <button
          type="button"
          className="sf-mode-pill"
          onClick={onCycleMode}
          style={pillStyle}
        >
          {MODE_LABEL[mode]}
        </button>
      </div>

      {/* Dials — only when the service is on (in/extra) */}
      {!off && (
        <div className="sf-dials" style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <Dial
            caption={leftCaption}
            value={leftValue}
            isNum
            onInc={leftSteppers ? () => onStepFreq(1) : undefined}
            onDec={leftSteppers ? () => onStepFreq(-1) : undefined}
          />
          <Dial
            caption={rightCaption}
            value={rightValue}
            isNum={rightIsNum}
            onInc={rightSteppers ? () => onStepDur(1) : undefined}
            onDec={rightSteppers ? () => onStepDur(-1) : undefined}
          />
        </div>
      )}

      {/* Optional note strip */}
      {note ? (
        <div
          className="sf-svc-note"
          style={{ marginTop: 8, fontSize: 11.5, color: 'var(--sf-ink-3)' }}
        >
          {note}
        </div>
      ) : null}

      {/* Optional add-on strip when mode === 'extra' */}
      {extra && feeStr ? (
        <div
          className="sf-svc-extra"
          style={{ marginTop: 8, fontSize: 11.5, fontWeight: 600, color: 'var(--sf-warn)' }}
        >
          Optional add-on · billed separately at <span className="num">{feeStr}</span>
        </div>
      ) : null}
    </div>
  );
}
